import {
  Grid,
  Typography,
  Box,
  Paper,
  Skeleton,
  Stack,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useState, useEffect } from "react";
import moment from "moment";
import {
  DataGrid,
  GridCheckIcon,
  GridCloseIcon,
  GridColDef,
  GridRenderCellParams,
  useGridApiRef,
} from "@mui/x-data-grid";
import apiClient from "services/apiClient";
import MDButton from "components/MDButton";
import { DatePicker } from "@mui/x-date-pickers";

const Invoicing = () => {
  const [invoices, setInvoices] = useState<Array<any>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const gridRef = useGridApiRef();
  const [startDate, setStartDate] = useState(moment().subtract(7,"d"));
  const [endDate, setEndDate] = useState(moment());
  const [isExported, setIsExported] = useState("all");

  const loadInvoices = () => {
    setIsLoading(true);
    setInvoices([]);
    let uri = `/finance/invoices?startDate=${startDate.utc().format("MM/DD/yyyy")}`;
    if(endDate) uri += `&endDate=${endDate.utc().format("MM/DD/yyyy")}`
    if (isExported == "true" || isExported == "false") uri += `&isExported=${isExported}`;

    apiClient
      .get(uri)
      .then((results) => {
        setInvoices(results as []);
      })
      .finally(() => setTimeout(() => setIsLoading(false), 200));
  };

  useEffect(() => {
    loadInvoices();
  }, [startDate, endDate, isExported]);

  const columns: GridColDef[] = [
    { field: "controlNumber", headerName: "Control Number", width: 140 },
    {
      field: "isExported",
      headerName: "Exported",
      width: 80,
      renderCell: (params: GridRenderCellParams<any, boolean>) =>
        params.value ? (
          <GridCheckIcon style={{ fill: "green" }} />
        ) : (
          <GridCloseIcon style={{ fill: "red", textAlign: "center" }} />
        ),
    },
    { field: "subcontracted", headerName: "Subcontracted", width: 130 },
    { field: "vanID", headerName: "Van ID", width: 100 },
    {
      field: "originArrive",
      headerName: "Origin Arrive",
      width: 130,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>
          {`${
            params.value
              ? new Date(params.value).toLocaleString([], {
                  hourCycle: "h23",
                  year: "2-digit",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""
          }`}
        </>
      ),
    },
    {
      field: "originDepart",
      headerName: "Origin Depart",
      width: 130,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>
          {`${
            params.value
              ? new Date(params.value).toLocaleString([], {
                  hourCycle: "h23",
                  year: "2-digit",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""
          }`}
        </>
      ),
    },
    { field: "dest1State", headerName: "Dest 1 State", width: 120 },
    { field: "dest1City", headerName: "Dest 1 City", width: 180 },
    {
      field: "dest1Arrive",
      headerName: "Dest 1 Arrive",
      width: 130,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>
          {`${
            params.value
              ? new Date(params.value).toLocaleString([], {
                  hourCycle: "h23",
                  year: "2-digit",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""
          }`}
        </>
      ),
    },
    {
      field: "dest1Depart",
      headerName: "Dest 1 Depart",
      width: 130,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>
          {`${
            params.value
              ? new Date(params.value).toLocaleString([], {
                  hourCycle: "h23",
                  year: "2-digit",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""
          }`}
        </>
      ),
    },
    { field: "dest2State", headerName: "Dest 2 State", width: 120 },
    { field: "dest2City", headerName: "Dest 2 City", width: 180 },
    {
      field: "dest2Arrive",
      headerName: "Dest 2 Arrive",
      width: 200,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>
          {`${
            params.value
              ? new Date(params.value).toLocaleString([], {
                  hourCycle: "h23",
                  year: "2-digit",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""
          }`}
        </>
      ),
    },
    {
      field: "dest2Depart",
      headerName: "Dest 2 Depart",
      width: 200,
      renderCell: (params: GridRenderCellParams<any, string>) => (
        <>
          {`${
            params.value
              ? new Date(params.value).toLocaleString([], {
                  hourCycle: "h23",
                  year: "2-digit",
                  month: "numeric",
                  day: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })
              : ""
          }`}
        </>
      ),
    },
    { field: "busted", headerName: "Busted", width: 120 },
    { field: "mileage", headerName: "Mileage", width: 130 },
    { field: "odChange", headerName: "OD Change", width: 130 },
    { field: "spike", headerName: "Spike", width: 120 },
    { field: "assistMiles", headerName: "Assist Miles", width: 130 },
    { field: "excessMiles", headerName: "Excess Miles", width: 130 },
    { field: "originWaitUnits", headerName: "Origin Wait Units", width: 160},
    { field: "inTransitWaitUnits", headerName: "In-Transit Wait Units", width: 180, renderCell: (params: GridRenderCellParams<any, number>) => {return params.value ? Math.round(params.value) : ''} },
    { field: "destWaitUnits", headerName: "Dest Wait Units", width: 160 },
    { field: "foodRestWaitUnits", headerName: "Food/Rest Wait Units", width: 180, renderCell: (params: GridRenderCellParams<any, number>) => {return params.value ? Math.round(params.value) : ''} },
    { field: "miscCharge", headerName: "Misc Charge", width: 140 },
    { field: "minTripCharge", headerName: "Min Trip Charge", width: 160 },
    { field: "meterCharge", headerName: "Meter Charge", width: 140 },
    { field: "dedicatedYardVan", headerName: "Dedicated Yard Van", width: 180 },
    { field: "dedicatedYardVanExtra", headerName: "Dedicated Yard Van Extra", width: 200 },
    { field: "notes", headerName: "Notes", width: 250 },
  ];

  const LoadingSkeleton = () => (
    <Box
      sx={{
        height: "fit-content",
        padding: "0px",
        margin: "0px",
        overflow: "hidden",
      }}
    >
      {[...Array(20)].map((_, index) => (
        <Skeleton key={index} variant="rectangular" sx={{ my: 2, mx: 2, overflow: "hidden" }} />
      ))}
    </Box>
  );

  const exportInvoices = () => {
    setIsLoading(true);
    let uri = `/finance/invoices/export?startDate=${startDate.utc().format("MM/DD/yyyy")}`;
    if (endDate) uri += `&endDate=${endDate.utc().format("MM/DD/yyyy")}`
    if (isExported == "true" || isExported == "false") uri += `&isExported=${isExported}`;
    
    apiClient.getBlob(uri)
    .then((data: any) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoices.xls`);

      // Append to the document body and click to trigger download
      document.body.appendChild(link);
      link.click();

      // Clean up and remove the link
      document.body.removeChild(link);
      loadInvoices();
    }).finally(()=>setIsLoading(false));
  };
  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <h1>Invoicing</h1>
        <MDButton onClick={exportInvoices}>Export</MDButton>
      </Box>
      <Box sx={{ mt: 2, overflow: "hidden" }}>
        <DatePicker
          sx={{ mt: 1, mb: 2 }}
          label="Start Date"
          value={startDate}
          onChange={(newValue) => setStartDate(moment(newValue))}
        />
        <DatePicker
          sx={{ mt: 1, ml:1, mb: 2 }}
          label="End Date"
          value={endDate}
          onChange={(newValue) => setEndDate(moment(newValue))}
        />
        <FormControl sx={{ mt: 1, ml: 1, p: 0, minWidth: 120 }}>
          <InputLabel id="isActive-label">Exported</InputLabel>
          <Select
            SelectDisplayProps={{ style: { padding: "0.75rem !important" as any } }}
            id="isActive"
            name="Exported"
            labelId="isActive-label"
            label="Exported"
            value={isExported || ""}
            onChange={(e) => {
              setIsExported(e.target.value);
            }}
            multiple={false}
            sx={{ p: 0 }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
        <Paper sx={{ width: "99%", height: isLoading ? "100vh" : "auto", overflow: "hidden" }}>
          <DataGrid
            getRowId={(row) => row.controlNumber}
            sx={{ overflow: "hidden", fontSize: ".95em" }}
            autoHeight={isLoading ? false : true}
            rows={isLoading ? [] : invoices}
            columns={columns}
            pagination
            initialState={{
              sorting: { sortModel: [{ field: "pickupTime", sort: "asc" }] },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            rowCount={invoices?.length ?? 0}
            disableColumnMenu
            slots={{
              loadingOverlay: LoadingSkeleton,
              noRowsOverlay: () => (
                <Stack height="100%" alignItems="center" justifyContent="center">
                  No Events
                </Stack>
              ),
            }}
            loading={isLoading}
            density="compact"
            apiRef={gridRef as any}
            getRowHeight={() => "auto"}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "Mui-even" : "Mui-odd")}
          />
        </Paper>
      </Box>
    </>
  );
};

export default Invoicing;
